import { useCallback, useEffect, useState } from 'react';
import { notionProxy } from '@/lib/notionProxy';

export interface NotionTask {
  id: string;
  content: string;
  status: string;
  area: string | null;
  source: string | null;
  dueDate: string | null;
  date: string | null;
  pomodorosPlanned: number | null;
  pomodorosActual: number | null;
  dailyNoteIds: string[];
  url: string;
}

interface UseNotionReturn {
  tasks: NotionTask[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  isConnected: boolean;
}

function extractPlainText(richText: { plain_text: string }[] | undefined): string {
  if (!richText || richText.length === 0) return '';
  return richText.map((t) => t.plain_text).join('');
}

function parseNotionPage(page: Record<string, unknown>): NotionTask {
  const props = page.properties as Record<string, Record<string, unknown>>;

  const title = props.Task?.title as { plain_text: string }[] | undefined;
  const status = props.Status?.status as { name: string } | null;
  const area = props.Area?.select as { name: string } | null;
  const source = props.Source?.select as { name: string } | null;
  const due = props.Due?.date as { start: string } | null;
  const date = props.Date?.date as { start: string } | null;
  const pomPlanned = props['Pomodoros Planned']?.number as number | null;
  const pomActual = props['Pomodoros Actual']?.number as number | null;
  const dailyNote = props['Daily Note']?.relation as { id: string }[] | undefined;

  return {
    id: page.id as string,
    content: extractPlainText(title),
    status: status?.name ?? 'Not started',
    area: area?.name ?? null,
    source: source?.name ?? null,
    dueDate: due?.start ?? null,
    date: date?.start ?? null,
    pomodorosPlanned: pomPlanned,
    pomodorosActual: pomActual,
    dailyNoteIds: dailyNote?.map((r) => r.id) ?? [],
    url: page.url as string,
  };
}

export function useNotion(token: string | null, databaseId: string | null): UseNotionReturn {
  const [tasks, setTasks] = useState<NotionTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    if (!token || !databaseId) {
      setTasks([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const today = new Date().toISOString().split('T')[0];

      const result = await notionProxy({
        token,
        action: 'query-tasks',
        databaseId,
        filter: {
          and: [
            {
              property: 'Status',
              status: { does_not_equal: 'Done' },
            },
            {
              or: [
                {
                  property: 'Date',
                  date: { equals: today },
                },
                {
                  property: 'Date',
                  date: { is_empty: true },
                },
              ],
            },
          ],
        },
        sorts: [
          { property: 'Status', direction: 'ascending' },
          { property: 'Date', direction: 'ascending' },
        ],
      });

      if (result.error) {
        throw new Error(
          typeof result.error === 'string' ? result.error : 'Failed to fetch Notion tasks'
        );
      }

      const results = (result.results as Record<string, unknown>[]) ?? [];
      const parsed = results.map(parseNotionPage);
      setTasks(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
      setTasks([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, databaseId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return {
    tasks,
    isLoading,
    error,
    refetch: fetchTasks,
    isConnected: !!token && !!databaseId,
  };
}

export async function updateNotionTaskStatus(
  token: string,
  pageId: string,
  status: 'Not started' | 'In progress' | 'Done'
): Promise<boolean> {
  try {
    const result = await notionProxy({
      token,
      action: 'update-page',
      pageId,
      properties: {
        Status: { status: { name: status } },
      },
    });
    return !result.error;
  } catch {
    return false;
  }
}

export async function incrementNotionPomodoros(token: string, pageId: string): Promise<boolean> {
  try {
    // Fetch latest value to avoid read-modify-write race conditions
    const page = await notionProxy({ token, action: 'get-page', pageId });
    if (page.error) return false;

    const props = (page as Record<string, unknown>).properties as Record<
      string,
      Record<string, unknown>
    >;
    const currentActual = (props?.['Pomodoros Actual']?.number as number) ?? 0;

    const result = await notionProxy({
      token,
      action: 'update-page',
      pageId,
      properties: {
        'Pomodoros Actual': { number: currentActual + 1 },
      },
    });
    return !result.error;
  } catch {
    return false;
  }
}

export async function logSessionToNotionDailyNote(
  token: string,
  dailyNotePageId: string,
  taskName: string,
  durationMins: number
): Promise<boolean> {
  try {
    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    const result = await notionProxy({
      token,
      action: 'append-blocks',
      blockId: dailyNotePageId,
      children: [
        {
          object: 'block',
          type: 'callout',
          callout: {
            icon: { type: 'emoji', emoji: '\uD83C\uDF45' },
            rich_text: [
              {
                type: 'text',
                text: {
                  content: `${durationMins}min focus session: ${taskName} (${timeStr})`,
                },
              },
            ],
          },
        },
      ],
    });
    return !result.error;
  } catch {
    return false;
  }
}
